/**
 * Phase 2 · Pure-JS helpers for AdminNewSale / AdminQuickSale.
 * ESM named exports so both webpack (production build) and babel-jest
 * can consume them. NO JSX — safe for direct Jest import.
 */

export function emptyStickerList(variant) {
  variant = variant || "gold_crumple";
  return {
    id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID().slice(0, 8)
        : String(Math.random()).slice(2, 10),
    variant, rawText: "", preview: null,
  };
}

export function emptyState(opts) {
  const o = opts || {};
  const keepHistorical = o.keepHistorical || false;
  const keepDate = o.keepDate || "";
  const keepSource = o.keepSource || "in_person";
  const keepCustomer = o.keepCustomer || null;
  return {
    guest: !keepCustomer,
    customer: keepCustomer || { customer_id: null, first_name: "", last_name: "", email: "", phone: "" },
    stickerLists: [emptyStickerList()],
    productLines: [],
    customLines: [],
    lineOverrides: {},
    perLineTaxable: {},
    entireSaleNonTaxable: false,
    saleDiscount: { kind: "none", value: 0 },
    payments: [],
    source: keepSource,
    isHistorical: keepHistorical,
    transactionDate: keepDate,
    receiptPreference: "email",
    notesAdmin: "",
  };
}

const r2 = (n) => Math.round(n * 100) / 100;

export function computeTotalsPreview(state) {
  const stickerAggregate = state.stickerLists.reduce((acc, list) => {
    const lines = list.preview?.snapshot?.lines || [];
    const rate = list.preview?.snapshot?.totals?.tax?.rate ?? 0.07;
    let subtotal = 0;
    let taxable = 0;
    lines.forEach((ln) => {
      const key = `${list.id}:${ln.code}`;
      const eff = state.lineOverrides[key]?.unit_price ?? ln.unit_price;
      const isTax = state.perLineTaxable[key] !== false;
      subtotal += eff * ln.qty;
      if (isTax) taxable += eff * ln.qty;
    });
    return { subtotal: acc.subtotal + subtotal, taxable: acc.taxable + taxable, rate: acc.rate || rate };
  }, { subtotal: 0, taxable: 0, rate: 0 });
  const productSubtotal = state.productLines.reduce((a, p) => a + p.qty * p.unit_price, 0);
  // Custom lines split into normal + historical_gross (tax-inclusive).
  // Historical rows carry their *entered gross*; we reverse-calc the
  // pre-tax portion so subtotal + tax = the exact gross the operator
  // wrote on the paper record.
  const customNormalSubtotal = state.customLines
    .filter((c) => !c.historical_gross)
    .reduce((a, c) => a + c.qty * c.unit_price, 0);
  const rate = stickerAggregate.rate || 0.07;
  const customHistoricalGross = state.customLines
    .filter((c) => c.historical_gross)
    .reduce((a, c) => a + c.qty * c.unit_price, 0);
  const customHistoricalPretax = r2(
    state.customLines
      .filter((c) => c.historical_gross)
      .reduce((a, c) => a + (c.taxable && !state.entireSaleNonTaxable
                              ? r2((c.qty * c.unit_price) / (1 + rate))
                              : c.qty * c.unit_price), 0)
  );
  const customHistoricalImplicitTax = r2(
    state.customLines
      .filter((c) => c.historical_gross && c.taxable && !state.entireSaleNonTaxable)
      .reduce((a, c) => a + (c.qty * c.unit_price
                              - r2((c.qty * c.unit_price) / (1 + rate))), 0)
  );
  const customSubtotal = customNormalSubtotal + customHistoricalPretax;
  const subtotal = stickerAggregate.subtotal + productSubtotal + customSubtotal;
  let discount = 0;
  if (state.saleDiscount.kind === "percentage" && state.saleDiscount.value > 0) {
    discount = subtotal * (state.saleDiscount.value / 100);
  } else if (state.saleDiscount.kind === "fixed" && state.saleDiscount.value > 0) {
    discount = Math.min(state.saleDiscount.value, subtotal);
  }
  const taxRate = stickerAggregate.rate || 0.07;
  let taxableSubtotal = 0;
  if (!state.entireSaleNonTaxable) {
    const productTaxable = state.productLines.reduce((a, p) => a + (p.taxable ? p.qty * p.unit_price : 0), 0);
    // Only NORMAL custom lines feed the rate-based taxable base;
    // historical_gross rows already carry their own implicit tax
    // (reverse-computed) — added below as a scalar.
    const customTaxable = state.customLines
      .filter((c) => !c.historical_gross)
      .reduce((a, c) => a + (c.taxable ? c.qty * c.unit_price : 0), 0);
    taxableSubtotal = stickerAggregate.taxable + productTaxable + customTaxable;
    // Prorate the discount against the taxable share ONLY for percentage
    // discounts. Fixed-dollar discounts are a post-tax cash reduction —
    // the entered dollar amount reduces the FINAL payable total by
    // exactly that amount; tax must NOT change as a side effect.
    if (subtotal > 0 && discount > 0 && state.saleDiscount.kind === "percentage") {
      taxableSubtotal = Math.max(0, taxableSubtotal - discount * (taxableSubtotal / subtotal));
    }
  }
  const rateBasedTax = state.entireSaleNonTaxable ? 0 : r2(taxableSubtotal * taxRate);
  const tax = r2(rateBasedTax + (state.entireSaleNonTaxable ? 0 : customHistoricalImplicitTax));
  const total = subtotal - discount + tax;
  return { subtotal: r2(subtotal), discount: r2(discount),
           taxableSubtotal: r2(taxableSubtotal), tax: r2(tax), total: r2(total) };
}

export function buildPayload(state) {
  const overrides = Object.entries(state.lineOverrides)
    .filter(([, v]) => v && v.unit_price !== undefined)
    .map(([key, v]) => {
      const code = key.includes(":") ? key.split(":", 2)[1] : key;
      return { code, unit_price: v.unit_price, reason: v.reason || null };
    });
  const perLineTax = Object.fromEntries(
    Object.entries(state.perLineTaxable).map(([key, v]) => {
      const code = key.includes(":") ? key.split(":", 2)[1] : key;
      return [code, v];
    })
  );
  const sticker_lists = state.stickerLists
    .filter((l) => (l.preview?.items?.length || 0) > 0)
    .filter((l) => l.variant !== "russia_2018")
    .map((l) => ({ variant: l.variant, items: l.preview.items }));
  // Russia 2018 Black Back sticker groups travel as `custom_lines[]`
  // because the manual-order variant pattern is WC26-only and the
  // Russia album is a parallel single-sticker universe. Metadata on
  // each emitted line matches the Russia Quick Sale sheet contract so
  // the backend `_bucket_key` short-circuits to `russia_2018` and
  // the Sales Dashboard reports Russia revenue distinctly.
  const russia_custom_lines = state.stickerLists
    .filter((l) => l.variant === "russia_2018"
                   && (l.preview?.snapshot?.lines?.length || 0) > 0)
    .flatMap((l) => (l.preview.snapshot.lines || []).map((ln) => {
      const key = `${l.id}:${ln.code}`;
      const eff = state.lineOverrides?.[key]?.unit_price;
      const unit_price = (eff !== undefined && eff !== null)
        ? Number(eff)
        : Number(ln.unit_price) || 0;
      const taxable = state.perLineTaxable?.[key] !== false;
      return {
        description: `RUSSIA 2018 · ${ln.code} — ${ln.sticker_name || ""} — ${ln.country || ""}`,
        qty: Number(ln.qty) || 1,
        unit_price,
        taxable,
        kind: "generic_sticker",
        sticker_code: ln.code,
        sticker_quantity: Number(ln.qty) || 1,
        sticker_quantity_known: true,
        country: ln.country || "",
        country_code: ln.country_code || "",
        album_slug: "panini-wc-russia-2018",
        variant: "black_back",
        category: "russia_2018",
        display_category: "russia_2018",
        display_category_label: "RUSSIA 2018",
      };
    }));
  // Pass through historical_gross flag on custom lines so the backend
  // reverse-tax path fires. Also forward generic_sticker metadata.
  const custom_lines = [
    ...(state.customLines || []).map((c) => ({
      id: c.id, description: c.description, qty: c.qty,
      unit_price: c.unit_price, taxable: c.taxable,
      kind: c.kind || undefined,
      sticker_quantity_known: c.sticker_quantity_known,
      sticker_quantity: c.sticker_quantity,
      historical_note: c.historical_note || undefined,
      historical_gross: c.historical_gross || undefined,
    })),
    ...russia_custom_lines,
  ];
  return {
    album_slug: "panini-fifa-world-cup-2026",
    customer: state.guest ? { customer_id: null } : {
      customer_id: state.customer.customer_id || null,
      first_name: state.customer.first_name || null,
      last_name:  state.customer.last_name  || null,
      email:      state.customer.email      || null,
      phone:      state.customer.phone      || null,
    },
    delivery: { code: "local_pickup" },
    jurisdiction: "US-FL",
    guest_sale: state.guest,
    is_historical_sale: state.isHistorical,
    sticker_lists,
    product_lines: state.productLines,
    custom_lines,
    line_price_overrides: overrides,
    taxability: { entire_sale_non_taxable: state.entireSaleNonTaxable, per_line: perLineTax },
    sale_discount: state.saleDiscount.kind === "none" ? null : {
      kind: state.saleDiscount.kind, value: state.saleDiscount.value,
    },
    receipt_preference: state.receiptPreference,
    meta: {
      source: state.source,
      original_order_date: state.isHistorical && state.transactionDate
        ? new Date(state.transactionDate + "T12:00:00Z").toISOString() : null,
      payment_status: state.payments.length > 0 ? "paid" : "pending",
      payment_method: state.payments[0]?.method || "cash",
      notify_customer: !state.isHistorical && state.receiptPreference === "email",
      payments: state.payments.map((p) => ({
        amount: Number(p.amount) || 0, method: p.method, reference: p.reference || null,
      })),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────
// Save Quote & Resume Later · Feb-2026
//
// Versioned (schema_version=1) cart snapshot. We DELIBERATELY DO NOT
// persist raw React state verbatim — only the fields required to rebuild
// the cart on resume. Forward-compatible: new frontend fields can be
// appended without a backend schema change; unknown fields on hydrate
// are ignored.
// ─────────────────────────────────────────────────────────────────────
export const SAVED_QUOTE_SCHEMA_VERSION = 1;

export function extractCartState(state) {
  // Preserve the exact `preview` payload on sticker_lists — the
  // backend pricing engine will run again at conversion time, but
  // the resumed cart must render the line items + totals the operator
  // saw at save time without a round-trip to `/orders/quote`.
  const sticker_lists = (state.stickerLists || []).map((l) => ({
    id: l.id,
    variant: l.variant,
    raw_text: l.rawText || "",
    preview: l.preview || null,
  }));
  const customer = state.guest
    ? { customer_id: null, first_name: "", last_name: "", email: "", phone: "" }
    : {
        customer_id: state.customer?.customer_id || null,
        first_name:  state.customer?.first_name  || "",
        last_name:   state.customer?.last_name   || "",
        email:       state.customer?.email       || "",
        phone:       state.customer?.phone       || "",
      };
  return {
    schema_version: SAVED_QUOTE_SCHEMA_VERSION,
    guest: !!state.guest,
    customer,
    sticker_lists,
    product_lines: (state.productLines || []).map((p) => ({ ...p })),
    custom_lines:  (state.customLines  || []).map((c) => ({ ...c })),
    line_overrides:    { ...(state.lineOverrides   || {}) },
    per_line_taxable:  { ...(state.perLineTaxable  || {}) },
    entire_sale_non_taxable: !!state.entireSaleNonTaxable,
    sale_discount: {
      kind:  state.saleDiscount?.kind  || "none",
      value: Number(state.saleDiscount?.value) || 0,
    },
    payments: (state.payments || []).map((p) => ({
      method: p.method, amount: Number(p.amount) || 0,
      reference: p.reference || null,
    })),
    source: state.source || "quick_sale",
    is_historical: !!state.isHistorical,
    transaction_date: state.transactionDate || "",
    receipt_preference: state.receiptPreference || "email",
    notes_admin: state.notesAdmin || "",
  };
}

export function hydrateFromCartState(cart) {
  const safe = cart || {};
  const stickerLists = (safe.sticker_lists || []).map((l) => ({
    id: l.id || (typeof crypto !== "undefined" && crypto.randomUUID
                  ? crypto.randomUUID().slice(0, 8)
                  : String(Math.random()).slice(2, 10)),
    variant: l.variant || "gold_crumple",
    rawText: l.raw_text || "",
    preview: l.preview || null,
  }));
  // Guarantee at least ONE sticker list entry so computeTotalsPreview
  // never divides by zero on resume (same invariant as emptyState).
  const effectiveStickerLists = stickerLists.length > 0 ? stickerLists : [emptyStickerList()];
  const cust = safe.customer || {};
  return {
    guest: safe.guest !== false ? true : false,
    customer: {
      customer_id: cust.customer_id || null,
      first_name:  cust.first_name  || "",
      last_name:   cust.last_name   || "",
      email:       cust.email       || "",
      phone:       cust.phone       || "",
    },
    stickerLists: effectiveStickerLists,
    productLines: (safe.product_lines || []).map((p) => ({ ...p })),
    customLines:  (safe.custom_lines  || []).map((c) => ({ ...c })),
    lineOverrides:    { ...(safe.line_overrides   || {}) },
    perLineTaxable:   { ...(safe.per_line_taxable || {}) },
    entireSaleNonTaxable: !!safe.entire_sale_non_taxable,
    saleDiscount: {
      kind:  safe.sale_discount?.kind  || "none",
      value: Number(safe.sale_discount?.value) || 0,
    },
    payments: (safe.payments || []).map((p) => ({
      method: p.method || "cash",
      amount: Number(p.amount) || 0,
      reference: p.reference || null,
    })),
    source: safe.source || "quick_sale",
    isHistorical: !!safe.is_historical,
    transactionDate: safe.transaction_date || "",
    receiptPreference: safe.receipt_preference || "email",
    notesAdmin: safe.notes_admin || "",
  };
}

export function summarizeCartForSave(state, totals) {
  const stickerCount = (state.stickerLists || []).reduce(
    (a, l) => a + ((l.preview?.snapshot?.lines) || []).reduce((s, ln) => s + (Number(ln.qty) || 0), 0),
    0,
  );
  const productCount = (state.productLines || []).reduce((a, p) => a + (Number(p.qty) || 0), 0);
  const customCount  = (state.customLines  || []).reduce((a, c) => a + (Number(c.qty) || 0), 0);
  const customer = state.guest
    ? { customer_id: null, display_name: null, email: null, phone: null }
    : {
        customer_id: state.customer?.customer_id || null,
        first_name:  state.customer?.first_name  || null,
        last_name:   state.customer?.last_name   || null,
        display_name: `${state.customer?.first_name || ""} ${state.customer?.last_name || ""}`.trim() || null,
        email:       state.customer?.email       || null,
        phone:       state.customer?.phone       || null,
      };
  return {
    totals_snapshot: {
      subtotal: Number(totals?.subtotal) || 0,
      discount: Number(totals?.discount) || 0,
      tax:      Number(totals?.tax)      || 0,
      total:    Number(totals?.total)    || 0,
      item_count: stickerCount + productCount + customCount,
    },
    customer_ref: customer,
  };
}

